import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";
import QuoteSla from "@/components/QuoteSla";
import BrushDivider from "@/components/BrushDivider";

const FLOW = ["SUBSCRIBE", "SUBMIT REQUEST", "WE CREATE", "REVIEW", "REVISIONS", "APPROVE", "DOWNLOAD", "NEXT REQUEST"];

export default function Subscriptions() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="subscriptions-page" className="pt-24 sm:pt-28">
      <Seo title="Creative Subscriptions" description="Monthly creative support for creators and businesses — design, editing and branding on tap. Creator, Pro and Studio tiers." path="/subscriptions" image="/og-subscriptions.png" />
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <Eyebrow>CREATIVE SUBSCRIPTIONS</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-balance">
          A creative team, <span className="text-arsenal">on tap.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Need one graphic? We can do that. Need a creative team every month? We can do that too.
          Subscribe instead of scoping every job from zero.
        </p>
        <QuoteSla className="mt-6" />
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-6">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <div
              data-testid={`plan-${p.slug}`}
              className={`relative h-full flex flex-col border p-8 transition-colors duration-300 ${
                p.highlighted
                  ? "border-arsenal bg-gradient-to-b from-arsenal/15 to-transparent shadow-[0_0_50px_rgba(229,9,20,0.15)]"
                  : "border-white/10 bg-ink-surface hover:border-white/25"
              }`}
            >
              {p.highlighted && (
                <span className="absolute -top-3 left-8 clip-tag bg-arsenal px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-white">MOST POPULAR</span>
              )}
              <h2 className="font-display text-2xl font-black tracking-wider">{p.name}</h2>
              <p className="mt-2 text-sm text-slate-400">{p.audience}</p>
              <div className="mt-6 pb-6 border-b border-white/10">
                {p.price_monthly != null ? (
                  <p className="font-display text-4xl font-black">${p.price_monthly}<span className="text-base text-slate-500 font-normal">/mo</span></p>
                ) : (
                  <p className="font-display text-2xl font-black tracking-wide text-white">REQUEST A QUOTE</p>
                )}
                <p className="mt-2 font-mono text-[11px] tracking-[0.15em] text-arsenal">{p.price_note?.toUpperCase()}</p>
              </div>
              <p className="mt-5 font-mono text-[11px] tracking-[0.15em] text-slate-500">{p.model?.toUpperCase()}</p>
              <ul className="mt-4 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-arsenal" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to={`/start-a-project?plan=${p.slug}`}
                data-testid={`plan-cta-${p.slug}`}
                className={`clip-btn mt-8 inline-flex items-center justify-center gap-2 font-display font-bold tracking-widest text-sm px-6 py-3.5 transition-colors ${
                  p.highlighted ? "bg-arsenal hover:bg-arsenal-bright text-white" : "border border-white/20 text-white hover:border-arsenal hover:text-arsenal"
                }`}
              >
                GET {p.name} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      <p className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 text-sm text-slate-500" data-testid="plans-pricing-note">
        Every plan is scoped and priced with you — nothing is charged until we've agreed the fit. Send a project request and we'll set you up.
      </p>

      <BrushDivider flip />

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24" data-testid="subscription-flow">
        <Reveal>
          <Eyebrow>HOW SUBSCRIPTIONS WORK</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl text-balance">
            Subscribe. Request. Create. <span className="text-arsenal">Repeat.</span>
          </h2>
        </Reveal>
        <div className="mt-12 flex flex-wrap items-stretch gap-3">
          {FLOW.map((step, i) => (
            <Reveal key={step} delay={i * 0.05} className="flex items-center gap-3">
              <div className="flex items-center gap-3">
                <div className={`border px-5 py-4 ${i === 0 ? "border-arsenal bg-arsenal/10" : "border-white/10 bg-ink-surface"}`}>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-arsenal">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display text-sm font-bold tracking-wider text-white mt-1">{step}</p>
                </div>
                {i < FLOW.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-arsenal/60" />}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-2xl text-sm text-slate-500 leading-relaxed">
            Plans can run on request limits, simultaneous active requests, credits, hours or a usage allowance — the model per tier is configured by the studio, not hard-coded. Revisions are part of the loop, not an extra.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-white/5 bg-ink-surface/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Only need one thing?</h2>
          <p className="mt-3 text-slate-400">One-off projects are always available — no subscription required.</p>
          <Link to="/pricing" data-testid="subscriptions-pricing-link" className="group mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-arsenal hover:text-arsenal-bright transition-colors">
            SEE QUOTE OPTIONS <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
